import type { AxiosRequestConfig } from 'axios';
import { env } from '@/shared/config/env';
import type { PaginatedResponse } from '@dentexe/types';

export type { PaginatedResponse };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly retryAfter?: number,
    public readonly details?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  static is(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  isValidationError(): this is ValidationError {
    return this instanceof ValidationError;
  }

  isNetworkError(): this is NetworkError {
    return this instanceof NetworkError;
  }

  isTimeoutError(): this is TimeoutError {
    return this instanceof TimeoutError;
  }

  isCancellationError(): this is CancellationError {
    return this instanceof CancellationError;
  }

  isSessionInvalidated(): boolean {
    return this.status === 401 && this.code === 'SESSION_INVALIDATED';
  }

  isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export class NetworkError extends ApiError {
  constructor() {
    super(0, 'Network error — check your connection', 'ERR_NETWORK');
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends ApiError {
  constructor() {
    super(0, 'Request timed out', 'TIMEOUT');
    this.name = 'TimeoutError';
  }
}

export class ValidationError extends ApiError {
  constructor(status: number, message: string, details: Record<string, string[]>) {
    super(status, message, 'VALIDATION_ERROR', undefined, details);
    this.name = 'ValidationError';
  }
}

export class CancellationError extends ApiError {
  constructor() {
    super(0, 'Request was cancelled', 'CANCELLED');
    this.name = 'CancellationError';
  }
}

export interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
}

export interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  retry: RetryConfig;
  enableLogging: boolean;
}

export const DEFAULT_CLIENT_CONFIG: ApiClientConfig = {
  baseURL: '',
  timeout: 30_000,
  retry: {
    maxRetries: 1,
    retryDelay: 500,
  },
  enableLogging: env.DEV,
};

export interface ApiRequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  skipSessionInvalidation?: boolean;
  _retryCount?: number;
}
